import { useState, useEffect, useCallback } from "react";
import { getAttachmentUrlByFilename } from "@utils/attachment.utils";

export interface UseAttachmentByFilenameReturn {
	url: string;
	loading: boolean;
	error: string | null;
	refetch: () => void;
}

/**
 * Extracts filename from path (e.g., "uploads/profile-123.jpg" -> "profile-123.jpg")
 */
const getFilenameFromPath = (filePathOrName: string): string => {
	if (!filePathOrName || typeof filePathOrName !== "string") return "";
	const trimmed = filePathOrName.trim();
	if (!trimmed) return "";
	// If it's already a full URL, don't process - return as is (hook will handle via API)
	if (trimmed.startsWith("http://") || trimmed.startsWith("https://") || trimmed.startsWith("blob:")) {
		return trimmed;
	}
	// Extract filename from path
	return trimmed.includes("/") ? trimmed.split("/").pop() || trimmed : trimmed;
};

/**
 * React hook for fetching attachment URLs by filename
 * @param filenameOrPath - The filename or file path from backend
 * @param enabled - Whether to fetch (default: true when filename is valid)
 * @returns UseAttachmentByFilenameReturn - Object containing URL, loading state, error, and refetch function
 */
export const useAttachmentByFilename = (
	filenameOrPath: string | null | undefined,
	options?: { enabled?: boolean }
): UseAttachmentByFilenameReturn => {
	const enabled = options?.enabled ?? true;
	const [url, setUrl] = useState<string>("");
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const filename = getFilenameFromPath(filenameOrPath || "");

	const fetchAttachment = useCallback(async () => {
		if (!enabled || !filename) {
			setUrl("");
			setError(null);
			return;
		}

		// If it's already a full URL, use directly
		if (filename.startsWith("http://") || filename.startsWith("https://") || filename.startsWith("blob:")) {
			setUrl(filename);
			setError(null);
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const result = await getAttachmentUrlByFilename(filename);
			const fileUrl = result.url || (result as { data?: string }).data || "";

			if (result.success && fileUrl && fileUrl.trim() !== "") {
				const validUrl =
					fileUrl.startsWith("http://") || fileUrl.startsWith("https://") ? fileUrl : `https://${fileUrl}`;
				setUrl(validUrl);
			} else {
				setError(result.message || "Failed to fetch attachment");
				setUrl("");
			}
		} catch (err) {
			console.error("Error fetching attachment:", err);
			setError("Failed to fetch attachment");
			setUrl("");
		} finally {
			setLoading(false);
		}
	}, [filename, enabled]);

	useEffect(() => {
		fetchAttachment();
	}, [fetchAttachment]);

	return {
		url,
		loading,
		error,
		refetch: fetchAttachment,
	};
};
